import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useMemo } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import MetaHead from '@/components/MetaHead';
import { isDarkTheme } from '@/constants/Colors';
import { fluid, landingPalette } from '@/components/Landing/landingTokens';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import LandingEditorialHero from '@/components/Landing/LandingEditorialHero';
import HeroProductPreview from '@/components/Landing/HeroProductPreview';
import { Section, useLanding } from '@/components/Landing/LandingKit';
import Text from '@/components/Typography/Text';
import LandingWorkMode from '@/components/Landing/LandingWorkMode';
import LandingMac from '@/components/Landing/LandingMac';
import LandingLearn from '@/components/Landing/LandingLearn';
import LandingBots from '@/components/Landing/LandingBots';
import LandingValueStory, { LandingChatSection } from '@/components/Landing/LandingValueStory';
import LandingPersonaLinks from '@/components/Landing/LandingPersonaLinks';
import LandingClosing from '@/components/Landing/LandingClosing';
import PricingComparison from '@/components/Pricing/PricingComparison';
import LivingMoment from '@/components/Landing/LivingMoment';
import PublicFooter from '@/components/PublicFooter';
import { content, landingPageStructuredData } from '@/lib/landingPageSeo';
import LandingFaq from '@/components/Landing/LandingFaq';
export default function LandingPage() {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const insets = useSafeAreaInsets();
  const { c, width } = useLanding();

  useLandingStyles();

  const containerStyle = useMemo(() => ({
    flex: 1,
    backgroundColor: landingPalette(isDark).page,
  }), [isDark]);

  return (
    <View style={containerStyle}>
      <MetaHead
        title={content.title}
        description={content.description}
        canonical="/"
        structuredData={landingPageStructuredData}
      />

      <ScrollView
        {...(Platform.OS === 'web' ? { dataSet: { landingScroll: 'true', landingTheme: isDark ? 'dark' : 'light' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole={safeAccessibilityRole('main')}
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <LandingEditorialHero topInset={insets.top} />
        <Section label="See what Gab AI can create">
          <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: fluid(width, 30, 48), lineHeight: Math.round(fluid(width, 35, 54)), letterSpacing: -1.4, textAlign: 'center', marginBottom: 14 }}>From an idea to something real.</Text>
          <Text tag="p" style={{ color: c.inkSoft, fontSize: 17, lineHeight: 26, textAlign: 'center', maxWidth: 560, alignSelf: 'center', marginBottom: 32 }}>A website, a film, a game, or code that gets the job done. Pick an example and see where a little direction can lead.</Text>
          <HeroProductPreview />
        </Section>
        <LandingLearn />
        <LandingWorkMode />
        <LandingChatSection />
        <LandingBots />
        <LivingMoment scene="family" />
        <LandingValueStory />
        <PricingComparison />
        <LandingMac />
        <LandingPersonaLinks />
        <LandingFaq />
        <LandingClosing />
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
